import { Injectable } from '@angular/core';
import { filter, take } from 'rxjs/operators';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents, PiiString } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp } from '../instrumentation/instrumented-http.service';
import { CAPErrorCode, CAPErrorInfo } from '../models/cap-error.model';
import { ErrorDialogErrorCategory, ErrorDialogErrorType } from '../models/error-dialog.model';
import { FileItem } from '../models/file-item.model';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { HttpResponseContentType } from '../models/http-response-content-type.enum';
import { ObservableScope } from '../models/observable-scope.enum';
import { AngularEvents } from '../shared-context/angular-events';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { LocService } from '../shared-context/loc.service';
import { ReactShellConfigService } from '../shared-context/react-shell-config.service';
import { UserContextService } from '../shared-context/user-context.service';
import { DOMInteractiveMarker } from '../utilities/dom-interactive-marker.utility';
import { createPopupDialog, listenForPopupClose } from '../utilities/popup-dialog.utility';
import { ScopableObservable, ScopableSubject } from '../utilities/scopable-subject.utility';
import { AccessToken, AccessTokenConsumer, AccessTokenInfo, AccessTokenResponseError, AccessTokens, AccessTokenService, CAPDialogData, ClientTokenFetchResponse, InitializationStatus } from './access-token.service';
import { AuthState, AuthStateService } from './auth-state.service';
import { ConfigParserService } from './config-parser.service';
import { ErrorDialogService } from './error-dialog.service';
import { EventService } from './event.service';
import { OfficeHomeMsaTokenService } from './office-home-msa-token.service';
import { Performance } from './performance.service';
import { WindowService } from './window.service';

interface CAPLoginMessage {
    capLoginMessage: 'CAP_LoginComplete' | 'CAP_Not_Authenticated';
}

@Injectable()
export class OfficeHomeAccessTokenService extends AccessTokenService {
    private readonly refreshApiUrls: Map<AccessTokenConsumer, string>;
    private readonly accessTokens: Map<string, AccessTokenInfo>;
    private readonly defaultResourceUrls: Map<AccessTokenConsumer, string>;
    private readonly pendingRefreshRequests: Map<string, AccessTokenRequest>;
    private readonly initializePrimaryTokensPromise: Promise<void>;
    private readonly initializeSecondaryTokensPromise: Promise<void>;
    private readonly secondaryTokensEnabled: boolean;
    private readonly tokensRequiringResourceUrl: Set<AccessTokenConsumer> = new Set<AccessTokenConsumer>([AccessTokenConsumer.OneDrive, AccessTokenConsumer.SharePoint, AccessTokenConsumer.MetaOS]);
    private readonly msaClientTokensEnabled: boolean;
    private readonly _messageResponses: ScopableSubject<CAPLoginMessage> = new ScopableSubject();

    private capDialogStartTime = 0;
    private pendingInteractiveRequest: Promise<void>;

    constructor(
        private readonly authStateService: AuthStateService,
        private readonly contextService: ContextService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly instrumentedHttp: InstrumentedHttp,
        private readonly eventService: EventService,
        private readonly configParserService: ConfigParserService,
        errorHandler: OfficeHomePageErrorHandler,
        private readonly instrumentationService: InstrumentationService,
        private readonly msaTokenService: OfficeHomeMsaTokenService,
        private readonly windowService: WindowService,
        private readonly reactShellConfigService: ReactShellConfigService,
        private readonly userContextService: UserContextService,
        private readonly errorDialogService: ErrorDialogService,
        private readonly locService: LocService,
        private readonly clientStartupConfigService: ClientStartupConfigService
    ) {
        super();
        this.init(errorHandler, AccessTokenFormat.JWT, AccessTokenSource.OfficeHome);
        this.refreshApiUrls = new Map<AccessTokenConsumer, string>();
        this.accessTokens = new Map<string, AccessTokenInfo>();
        this.pendingRefreshRequests = new Map<string, AccessTokenRequest>();
        this.secondaryTokensEnabled = this.capabilitiesService.capabilities.secondaryTokensEnabled;
        this.msaClientTokensEnabled = this.capabilitiesService.capabilities.msaClientTokensEnabled;

        this.defaultResourceUrls = new Map<AccessTokenConsumer, string>();
        this.defaultResourceUrls.set(AccessTokenConsumer.OneDrive, this.contextService.sharedContext.oneDriveResourceId);
        this.defaultResourceUrls.set(AccessTokenConsumer.SharePoint, this.contextService.sharedContext.sharePointResourceId);

        if (this.msaClientTokensEnabled) {
            if (!this.clientStartupConfigService.config.isV2Auth || !this.capabilitiesService.capabilities.graphApiForV2ConsumerEnabled) {
                // We can initialize tokens fetched through MSA Implicit Grant flow immediately.
                this.initializeAccessToken(AccessTokenConsumer.OneDrive, '', null);
                this.initializeAccessToken(AccessTokenConsumer.Graph, '', null);
            }
        }

        if (this.capabilitiesService.capabilities.clientTokenFetchEnabled) {
            // If initial client primary token fetch is enabled, then we will initialize the secondary tokens immediately and fetch them on demand
            // through the refresh token APIs.
            this.initializeSecondaryTokensPromise = Promise.resolve();
            if (this.secondaryTokensEnabled) {
                this.initializeSecondaryTokens(null);
            }

            this.initializePrimaryTokensPromise = new Promise(resolve => {
                const url = `${this.contextService.sharedContext.officeHomeApiRoot}/api/primaryTokens?pageUrl=${encodeURIComponent(this.windowService.nativeWindow.location.href)}`;
                const requestConfig: HttpRequestOptions = {
                    responseType: HttpResponseContentType.Json,
                    headers: new Map()
                };
                this.instrumentedHttp
                    .get<ClientTokenFetchResponse>(KnownInstrumentationEvents.PrimaryTokenFetchRequest, url, requestConfig, FetchStrategy.NetworkOnly)
                    .then(({ data }) => {
                        if (data) {
                            // This is essentially reproducing the functionality in HomeController.cs where we redirect to login if needed.
                            if (data.ReauthRequired && data.LoginUrl) {
                                this.windowService.nativeWindow.location.href = data.LoginUrl;
                                return;
                            }
                        }

                        this.initializePrimaryTokens(data && data.Tokens);
                        resolve();
                    })
                    .catch(error => {
                        // Make sure to initialize the tokens and resolve even on failure so that refresh calls work.
                        this.errorHandler.handleError(error, ErrorId.Pno9F);
                        this.initializePrimaryTokens(null);
                        resolve();
                    });
            });
        } else {
            // IMPORTANT: This service gets initial values for access tokens from config which is included with page response.
            // With page response arriving in multiple chunks, it is possible that tokens config data is not available yet.
            // Thus initialization is done as a promise which will get resolved once config is available.
            this.initializePrimaryTokensPromise = this.initializeTokensPromise('primaryTokensInfo', AngularEvents.primaryTokensLoaded, (tokensConfig: AccessTokens) => {

                // This indicates that the third chunk never made it down to the client.
                if (!tokensConfig) {
                    const params: InstrumentationParams = {};
                    params[InstrumentationParam.ElementId] = 'primaryTokensInfo';
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.TokensConfigMissing, params);
                }

                // IMPORTANT: On rare occasions we have seen failure to parse config. In this case we will initialize tokens with
                // empty values. The tokens will be fetched by hitting office.com refresh token API when explicitly required.
                this.initializePrimaryTokens(tokensConfig);

                if (!this.secondaryTokensEnabled && !this.msaClientTokensEnabled) {
                    this.initializeSecondaryTokens(tokensConfig);
                }
            });

            if (this.secondaryTokensEnabled) {
                this.initializeSecondaryTokensPromise = this.initializeTokensPromise('secondaryTokensInfo', AngularEvents.secondaryTokensLoaded, (tokensConfig: AccessTokens) => {
                    // This indicates that the fourth chunk never made it down to the client.
                    if (!tokensConfig) {
                        const params: InstrumentationParams = {};
                        params[InstrumentationParam.ElementId] = 'secondaryTokensInfo';
                        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.TokensConfigMissing, params);
                    }

                    this.initializeSecondaryTokens(tokensConfig);
                });
            } else {
                this.initializeSecondaryTokensPromise = Promise.resolve();
            }
        }

        const shellTokenRequestDictionary = this.windowService.nativeWindow.shellTokenRequestDictionary;

        if (this.reactShellConfigService.reactShellConfig.consumerShellServiceEnabled && shellTokenRequestDictionary) {
            shellTokenRequestDictionary[this.contextService.sharedContext.consumerShellServiceEndpoint] = () => this.getAccessTokenResponse(AccessTokenConsumer.ConsumerShellService).then(response => response.token);
        }

        this.windowService.nativeWindow.addEventListener('message', event => {
            const { origin, data } = event;

            if (this.windowService.sameOriginRegex.test(origin)) {
                this._messageResponses.next(data);
            }
        });
    }

    public hasPermanentAuthError(): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            // Wait for initialization to complete to extract corresponding field from config
            this.initializePrimaryTokensPromise
                .then(() => {
                    let allTokensWithPermanentError = this.accessTokens.size > 0;
                    this.accessTokens.forEach(tokenInfo => {
                        if (allTokensWithPermanentError && tokenInfo.token && !tokenInfo.token.HasPermanentError) {
                            allTokensWithPermanentError = false;
                        }
                    });

                    resolve(allTokensWithPermanentError);
                });
        });
    }

    public hasOneDriveToken(): boolean {
        const tokenInfo = this.accessTokens.get(this.getTokenKey(AccessTokenConsumer.OneDrive, null));
        return !!tokenInfo && !!tokenInfo.token && !!tokenInfo.token.TokenValue;
    }

    public hasCAPError(tokenConsumer: AccessTokenConsumer, resourceUrl?: string): boolean {
        const tokenKey = this.getTokenKey(tokenConsumer, resourceUrl);
        const tokenInfo = this.accessTokens.get(tokenKey);
        return tokenInfo && tokenInfo.token && tokenInfo.token.HasCAPError;
    }

    protected getAccessTokenResponse(
        tokenConsumer: AccessTokenConsumer,
        forceFetch: boolean = false,
        // resourceUrl is only required when access token is requested for OneDrive, SharePoint and MetaOS,
        // all other tokens have their url pre-mapped to a resource endpoint that can be fetched by tokenConsumer
        // e.g.Graph token's resourceUrl is mapped to https://graph.microsoft.com
        resourceUrl: string = '',
        allowInteractive = false,
        capDialogData: CAPDialogData = null,
        _clientId?: string,
        // scope is a param required for v2.0 endpoint, /.default scope value refers to the static list of permission configured on the application registration
        // we can use scope value of /.default for both v1 and v2 endpoint to request token that includes scopes for all preauth permission
        // For now only graph token needs non-default scope to request different CAP permission, all other tokens use /.default value.
        scope?: string,
        claims?: Array<string>
    ): Promise<AccessTokenResponse> {
        const tokenKey = this.getTokenKey(tokenConsumer, resourceUrl, scope, claims);
        const pendingRefreshRequest = this.pendingRefreshRequests.get(tokenKey);

        // Only use the pending request if this is a non-interactive request or if the pending request is already interactive.
        // This ensures we show the interactive popup experience even if a non-interactive request for the same token was triggered
        // at the same time.
        // For example, this can happen during create new document deep link scenarios for MSA users where we try fetching
        // a OneDrive token when both creating the new doc and initializing the upload button.
        if (pendingRefreshRequest && pendingRefreshRequest.pending && (!allowInteractive || pendingRefreshRequest.allowInteractive)) {
            return pendingRefreshRequest.result;
        }

        const accessTokenRequest: AccessTokenRequest = {
            pending: false,
            result: null,
            allowInteractive
        };

        const resultPromise = new Promise<AccessTokenResponse>((resolve, reject) => {
            // Wait for initialization to complete before deciding on whether to fetch token or use the one that came as part of page response.
            // For MSA token fetches, we don't need to wait for full page response, so this promise will be a no-op in that case.
            this.getInitializationPromise(tokenConsumer, allowInteractive)
                .then(() => {
                    let tokenInfo = this.accessTokens.get(tokenKey);

                    // Initialize token info for resource that did not have token inside page response
                    if (!tokenInfo) {
                        tokenInfo = {
                            refreshEndpoint: this.refreshApiUrls.get(tokenConsumer),
                            token: { TokenValue: '', ExpiryMs: this.getDefaultExpiry() },
                            resourceUrl
                        };

                        this.accessTokens.set(tokenKey, tokenInfo);
                    }

                    forceFetch = forceFetch || tokenInfo.forceRefresh;
                    if (forceFetch || !this.tokenIsValid(tokenInfo)) {
                        const capHelper = (resourceId: string, scopes: Array<string>, capSubErrorCode: CAPErrorCode, _fallbackReject: () => void, _fetchAccessToken: (b: boolean, b2: boolean) => void) => {
                            this.startCAPFlow(resourceId, scopes, capSubErrorCode, capDialogData)
                                .then(() => {
                                    // Mark all tokens for force refresh from AAD. This is so they are updated with the latest
                                    // claims after reauth.
                                    this.accessTokens.forEach((ti: AccessTokenInfo) => {
                                        ti.forceRefresh = true;
                                        ti.token.TokenValue = '';
                                        ti.token.HasCAPError = false;
                                        ti.token.HasPermanentError = false;
                                        ti.token.CAPSubErrorCode = null;
                                    });

                                    // at this point new auth cookies will have been set. force refresh the token
                                    _fetchAccessToken(true, true);
                                    this._reauth.next();
                                })
                                .catch(_fallbackReject);
                        };

                        let retryAttempt = 0;
                        const fetchAccessToken = (_bypassCache: boolean, _isCAPRetry: boolean) => {
                            const onError = (reason: AccessTokenError | string, retriable: boolean, statusCode?: number) => {
                                const error = typeof reason === 'string' ? new AccessTokenResponseError(reason, statusCode) : reason;
                                if (retriable && retryAttempt < this.contextService.sharedContext.accessTokenMaxRetryAttempts) {
                                    ++retryAttempt;
                                    fetchAccessToken(_bypassCache, _isCAPRetry);
                                } else {
                                    reject(error);
                                }
                            };

                            if (this.useMsaTokenService(tokenConsumer)) {
                                this.msaTokenService.getAccessTokenResponse(tokenConsumer, allowInteractive)
                                    .then(response => {
                                        if (!response.error && response.token) {
                                            this.refreshToken(tokenInfo, response.token, response.expiryMs);
                                            resolve(response);
                                        } else {
                                            onError(response.error, false); // TODO: retry on some errors
                                        }
                                    })
                                    .catch(err => {
                                        onError(err, false);
                                    });
                                return;
                            }

                            if (!resourceUrl) {
                                resourceUrl = tokenInfo.resourceUrl;
                            }

                            if (!resourceUrl && this.tokensRequiringResourceUrl.has(tokenConsumer)) {
                                onError(`Missing resource url, but is required for <${tokenConsumer}>.`, false);
                                return;
                            }

                            const requestConfig: HttpRequestOptions = {
                                responseType: HttpResponseContentType.Json,
                                headers: new Map()
                            };

                            if (resourceUrl) {
                                requestConfig.headers.set('X-OfficeHome-ResourceUrl', resourceUrl);
                            }

                            const customScope = scope === undefined ? '' : `&scope=${scope}`;
                            const customClaims = (claims === undefined || claims.length === 0) ? '' : '&claims=' + claims.join('_');
                            const url = `${this.contextService.sharedContext.officeHomeApiRoot}/${tokenInfo.refreshEndpoint}${_bypassCache ? '?bypassCache=true' : '?bypassCache=false'}${customScope}${customClaims}`;
                            const capDialogCategory = capDialogData?.category;
                            this.instrumentedHttp
                                .get<AccessToken>(KnownInstrumentationEvents.RefreshAccessTokenRequest, url, requestConfig, FetchStrategy.NetworkOnly)
                                .instrument((_result, params) => {
                                    params[InstrumentationParam.Action] = _isCAPRetry ? 'CAP_Retry' : 'Regular';
                                    params[InstrumentationParam.State] = _bypassCache ? 'Bypass_Cache' : 'Regular';
                                    params[InstrumentationParam.Consumer] = tokenConsumer;
                                    params[InstrumentationParam.Retry] = retryAttempt.toString();
                                    if (resourceUrl) {
                                        params[InstrumentationParam.ResourceUrl] = new PiiString(resourceUrl);
                                        let docType = FileItem.referralFromFileLocation(resourceUrl);
                                        if (!docType && FileItem.isConsumerDocument(resourceUrl)) {
                                            docType = 'Consumer';
                                        }
                                        params[InstrumentationParam.DocumentType] = docType;
                                    }
                                })
                                .then(({ data }) => {
                                    const token = data;
                                    if (token && token.TokenValue) {
                                        this.refreshToken(tokenInfo, token.TokenValue, token.ExpiryMs);
                                        if (_isCAPRetry) {
                                            this.logCAPDialogEvent('token refresh succeeded', capDialogCategory);
                                        }
                                        resolve({ success: true, token: token.TokenValue, expiryMs: token.ExpiryMs, format: AccessTokenFormat.JWT, source: AccessTokenSource.OfficeHome });
                                    } else {
                                        if (_isCAPRetry) {
                                            this.logCAPDialogEvent('token refresh empty token', capDialogCategory);
                                        }
                                        onError('Response did not contain valid token', true);
                                    }
                                })
                                .catch(error => {
                                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                                    this.errorHandler.handleError(error, ErrorId.SgxOi);

                                    if (_isCAPRetry) {
                                        this.logCAPDialogEvent('token refresh failed', capDialogCategory);
                                    }

                                    if (!error) {
                                        onError('Request failed', true);
                                        return;
                                    }

                                    const errorResponse = error.data as AccessToken;
                                    const status = error.status;
                                    // Forbidden (403) is returned when request does not carry valid auth cookie
                                    // (expired or cleared) and it is not retriable
                                    // 400 is returned for requests that cannot be retried due to bad input or admin settings.
                                    // 429 is returned when AAD throttled the request. Don't retry to avoid making throttling worse.
                                    // If this request was for fetching a token after completing the CAP popup, don't trigger the popup again.
                                    if (errorResponse && errorResponse.HasCAPError) {
                                        const fallbackReject = () => onError('CAP_Error', false);
                                        if (allowInteractive && this.capabilitiesService.capabilities.capPopupEnabled && !_isCAPRetry && errorResponse.CAPSubErrorCode && errorResponse.ResourceId && capDialogData) {
                                            capHelper(errorResponse.ResourceId, errorResponse.Scopes, errorResponse.CAPSubErrorCode, fallbackReject, fetchAccessToken);
                                        } else {
                                            fallbackReject();
                                        }
                                    } else {
                                        const shouldRetry = (status >= 500 && status < 600) || status === 0 || status === 707 || status === 706;
                                        onError(`Request failed with error code: ${status}`, shouldRetry, status);
                                    }
                                });
                        };

                        // Check before HasPermanentError to ensure best error message is shown.
                        if (tokenInfo.token && tokenInfo.token.HasCAPError) {
                            const fallbackReject = () => reject(new AccessTokenResponseError('CAP_Error'));
                            if (allowInteractive && this.capabilitiesService.capabilities.capPopupEnabled && tokenInfo.token.CAPSubErrorCode && tokenInfo.token.ResourceId && capDialogData) {
                                if (this.clientStartupConfigService.config.authType === AuthType.OrgId || (this.clientStartupConfigService.config.authType === AuthType.MSA && tokenConsumer === AccessTokenConsumer.Graph)) {
                                    capHelper(tokenInfo.token.ResourceId, tokenInfo.token.Scopes, tokenInfo.token.CAPSubErrorCode, fallbackReject, fetchAccessToken);
                                }
                            } else {
                                fallbackReject();
                            }
                            return;
                        }

                        // Check if there was permanent auth error detected while generating page response on server.
                        // If this is the case then fail fast because refresh will fail anyway without reauthentication
                        // which will be triggered on timeout (see AuthStateService.watchAccessTokenPermanentError for details)
                        if (tokenInfo.token && tokenInfo.token.HasPermanentError) {
                            reject(new AccessTokenResponseError(`Permanent auth error has been detected; refresh token for <${tokenConsumer}> is aborted.`));
                            return;
                        }

                        // Check current auth state: if user is no longer authenticated then fail fast without
                        // making server call
                        if (this.authStateService.getCurrentState() !== AuthState.SignedIn) {
                            reject(new AccessTokenResponseError(`User has to be signed in; refresh token for <${tokenConsumer}> is aborted.`));
                            return;
                        }

                        if (tokenConsumer === AccessTokenConsumer.OfficeHome) {
                            // Don't refresh the OfficeHome token. We will fallback to cookie-based auth and have the OfficeHome API being called return a fresh token.
                            // This will save one round-trip to server.
                            reject(new AccessTokenResponseError('Not refreshing OfficeHome token'));
                            return;
                        }

                        // Force a full token refresh (bypass Redis) if we already have an old, invalid token, or if explicitly calling for a force fetch.
                        // For cases where we don't have an old token, for example if fetching a Sharepoint/OneDrive token for the first time, it is possible
                        // that a valid token exists in Redis, so don't force a full refresh.
                        const bypassCacheWhenFetchingToken = forceFetch || !!tokenInfo.token.TokenValue;
                        fetchAccessToken(bypassCacheWhenFetchingToken, false);
                    } else {
                        resolve({ success: true, token: tokenInfo.token.TokenValue, expiryMs: tokenInfo.token.ExpiryMs, format: AccessTokenFormat.JWT, source: AccessTokenSource.OfficeHome });
                    }
                })
                .catch(error => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    this.errorHandler.handleError(error, ErrorId.FZWPi);

                    reject(new AccessTokenResponseError(`Token for <${tokenConsumer}> does not have initialization info.`));
                });
        }).then(token => {
            accessTokenRequest.pending = false;
            return token;
        }).catch(error => {
            accessTokenRequest.pending = false;
            throw error;
        });

        accessTokenRequest.pending = true;
        accessTokenRequest.result = resultPromise;
        this.pendingRefreshRequests.set(tokenKey, accessTokenRequest);

        return resultPromise;
    }

    private useMsaTokenService(token: AccessTokenConsumer): boolean {
        return this.msaClientTokensEnabled && this.msaTokenService.supportsToken(token);
    }

    private getTokenKey(tokenType: AccessTokenConsumer, resourceUrl: string, scope: string = '.default', claims?: Array<string>): string {
        if (!resourceUrl && this.defaultResourceUrls.has(tokenType)) {
            resourceUrl = this.defaultResourceUrls.get(tokenType);
        }

        if (claims && claims.length > 0) {
            return tokenType + (resourceUrl ? `_${resourceUrl}` : '') + `_${scope}` + `_${claims.join('_')}`;
        }
        return tokenType + (resourceUrl ? `_${resourceUrl}` : '') + `_${scope}`;
    }

    private tokenIsValid(tokenInfo: AccessTokenInfo): boolean {
        return tokenInfo && tokenInfo.token && tokenInfo.token.TokenValue && tokenInfo.token.ExpiryMs > new Date().getTime();
    }

    private refreshToken(tokenInfo: AccessTokenInfo, newToken: string, newTokenExp: number) {
        tokenInfo.token.TokenValue = newToken;
        tokenInfo.token.ExpiryMs = newTokenExp || this.getDefaultExpiry();
        tokenInfo.forceRefresh = false;
    }

    private startCAPFlow(resourceId: string, scopes: Array<string>, capErrorCode: CAPErrorCode, capDialogData: CAPDialogData): Promise<void> {
        this.pendingInteractiveRequest = new Promise((resolve, reject) => {
            const _resolve = () => {
                this.pendingInteractiveRequest = null;
                resolve();
            };

            const _reject = () => {
                this.pendingInteractiveRequest = null;
                reject();
            };

            const errorType = this.clientStartupConfigService.config.authType === AuthType.MSA ? ErrorDialogErrorType.MSAGraphCapError : ErrorDialogErrorType.ConditionalAccess;
            this.errorDialogService.showErrorDialog(
                {
                    category: capDialogData?.category,
                    error: errorType,
                    fileType: capDialogData?.fileType,
                    errorInfo: { capErrorCode } as CAPErrorInfo,
                    onActionClicked: () => this.openCAPPopup(resourceId, scopes, capDialogData?.category, _resolve, _reject),
                    onClose: _reject
                });
        });

        return this.pendingInteractiveRequest;
    }

    private openCAPPopup(resourceId: string, scopes: Array<string>, category: ErrorDialogErrorCategory, resolve: () => void, reject: () => void) {
        const isV2 = this.clientStartupConfigService.config.isV2Auth;
        const resource = isV2 ? encodeURIComponent(this.createResourceWithScopes(resourceId, scopes)) : resourceId;

        const loginUrl = this.locService.format(this.contextService.sharedContext.tokenLoginUrlTemplate, resource);
        this.capDialogStartTime = Performance.now();
        const popUpWindow = createPopupDialog(loginUrl, this.windowService.nativeWindow);
        if (!popUpWindow) {
            this.logCAPDialogEvent('Could not open popup', category);
            reject();
            return;
        }

        this.logCAPDialogEvent('Popup opened', category);

        const popupChecker = listenForPopupClose(popUpWindow, this.windowService.nativeWindow, () => {
            this.logCAPDialogEvent('Popup closed by user', category);
            reject();
        });

        const responsePromise = this.messageResponses.withScope(ObservableScope.OnlyFuture).pipe(
            filter((response: CAPLoginMessage) => !!response && !!response.capLoginMessage),
            take(1)
        ).toPromise();

        responsePromise.then((message: CAPLoginMessage) => {
            this.windowService.nativeWindow.clearInterval(popupChecker);
            popUpWindow.close();
            this.logCAPDialogEvent(message.capLoginMessage, category);
            if (message.capLoginMessage === 'CAP_LoginComplete') {
                resolve();
            } else {
                reject();
            }
        });
    }

    private logCAPDialogEvent(message: string, category: ErrorDialogErrorCategory) {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.ItemCategory] = category;
        params[InstrumentationParam.State] = message;
        params[InstrumentationParam.Latency] = Math.ceil(Performance.now() - this.capDialogStartTime).toString();
        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.CAPPopup, params);
    }

    private getInitializationPromise(tokenConsumer: AccessTokenConsumer, allowInteractive: boolean): Promise<void> {
        if (this.useMsaTokenService(tokenConsumer)) {
            return Promise.resolve();
        }

        let initPromise: Promise<void>;
        if (this.secondaryTokensEnabled && (tokenConsumer === AccessTokenConsumer.OneDrive || tokenConsumer === AccessTokenConsumer.SharePoint || tokenConsumer === AccessTokenConsumer.Exchange)) {
            initPromise = this.initializeSecondaryTokensPromise;
        } else {
            initPromise = this.initializePrimaryTokensPromise;
        }

        if (allowInteractive && this.pendingInteractiveRequest) {
            return Promise.all([initPromise, this.pendingInteractiveRequest]).catch().then();
        }

        return initPromise;

    }

    private initializeTokensPromise(configElementId: string, tokensLoadedEvent: AngularEvents, initializeTokensFromConfig: (config: AccessTokens) => void): Promise<void> {
        return new Promise<void>(resolve => {

            let initializationStatus = InitializationStatus.NotInitialized;
            const initializeTokens = (checkForTokensInfoLoadedIndicatorPresence: boolean) => {
                if (initializationStatus !== InitializationStatus.NotInitialized) {
                    return false;
                }

                // Check if access tokens config is already available. If element with auth tokens config is not found then
                // it is indication that chunk with the config has not been received from server yet. The initialization
                // attempt is aborted immediately; it will be attempted again after getting notification that will be broadcasted
                // using Angular event notification mechanism.
                if (checkForTokensInfoLoadedIndicatorPresence && !document.getElementById(configElementId)) {
                    return false;
                }

                initializationStatus = InitializationStatus.Initializing;

                const accessTokensFromConfig = this.configParserService.parse<AccessTokens>(configElementId);
                initializeTokensFromConfig(accessTokensFromConfig);

                resolve();
                initializationStatus = InitializationStatus.Initialized;

                return true;
            };

            // Attempt initializing tokens immediately; this will only succeed if response chunk with tokens config have been received and
            // processed by DOM parser by the time this method is called.
            if (!initializeTokens(true)) {
                // If initialization failed then tokens config has not been parsed yet. Listen for event broadcasted from the script which is
                // executed right after tokens config becomes available.
                this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                    filter(event => event.name === tokensLoadedEvent),
                    take(1))
                    .toPromise()
                    .then(() => initializeTokens(false));

                // If for whatever reason there was a failure to broadcast tokensInfoLoaded event then initialize tokens upon
                // parsing of the entire response
                DOMInteractiveMarker.shellAndConfigsReady().then(() => initializeTokens(false));
            }
        });
    }

    private initializePrimaryTokens(tokensConfig: AccessTokens) {
        this.initializeAccessToken(AccessTokenConsumer.BingAnswers, 'api/refreshBingAnswersToken/v2', tokensConfig && tokensConfig.BingAnswers);
        this.initializeAccessToken(AccessTokenConsumer.Graph, 'api/refreshGraphAccessToken/v2', tokensConfig && tokensConfig.Graph);
        this.initializeAccessToken(AccessTokenConsumer.Loki, 'api/refreshLokiToken/v2', tokensConfig && tokensConfig.Loki);
        this.initializeAccessToken(AccessTokenConsumer.MruPlusPlus, 'api/refreshMruPlusPlusToken/v2', tokensConfig && tokensConfig.Mru);
        this.initializeAccessToken(AccessTokenConsumer.OneNote, 'api/refreshOneNoteToken/v2', tokensConfig && tokensConfig.OneNote);
        this.initializeAccessToken(AccessTokenConsumer.Portal, 'api/refreshPortalToken/v2', tokensConfig && tokensConfig.Portal);
        this.initializeAccessToken(AccessTokenConsumer.QueryFormulation, 'api/refreshQueryFormulationToken/v2', tokensConfig && tokensConfig.QueryFormulation);
        this.initializeAccessToken(AccessTokenConsumer.Substrate, 'api/refreshSubstrateToken/v2', tokensConfig && tokensConfig.Substrate);
        this.initializeAccessToken(AccessTokenConsumer.Sway, 'api/refreshSwayToken/v2', tokensConfig && tokensConfig.Sway);
        this.initializeAccessToken(AccessTokenConsumer.Forms, 'api/refreshFormsToken/v2', tokensConfig && tokensConfig.Forms);
        this.initializeAccessToken(AccessTokenConsumer.MyApps, 'api/refreshMyAppsToken/v2', tokensConfig && tokensConfig.MyApps);
        this.initializeAccessToken(AccessTokenConsumer.ConsumerShellService, 'api/refreshConsumerShellServiceToken/v2', tokensConfig && tokensConfig.ConsumerShellService);
        this.initializeAccessToken(AccessTokenConsumer.FluidPushChannel, 'api/refreshFluidPushChannelToken/v2', tokensConfig && tokensConfig.FluidPushChannel);
        this.initializeAccessToken(AccessTokenConsumer.AugLoop, 'api/refreshAugLoopToken/v2', tokensConfig && tokensConfig.AugLoop);
        !this.userContextService.user.isGcc && this.initializeAccessToken(AccessTokenConsumer.Ods, 'api/refreshOdsToken/v2', tokensConfig && tokensConfig.Ods);
        this.initializeAccessToken(AccessTokenConsumer.TenantFeedback, 'api/refreshTenantFeedbackToken/v2', tokensConfig && tokensConfig.TenantFeedback);
        this.initializeAccessToken(AccessTokenConsumer.Notify, 'api/refreshNotifyToken/v2', tokensConfig && tokensConfig.Notify);
        this.initializeAccessToken(AccessTokenConsumer.Yammer, 'api/refreshYammerToken/v2', tokensConfig && tokensConfig.Yammer);
        this.initializeAccessToken(AccessTokenConsumer.MetaOS, 'api/refreshMetaOSToken/v2', null);
        this.initializeAccessToken(AccessTokenConsumer.MfsService, 'api/refreshMfsServiceToken/v2', tokensConfig && tokensConfig.MfsService);

        this.initializeAccessToken(AccessTokenConsumer.OfficeHome, null, tokensConfig && tokensConfig.OfficeHome);
    }

    private initializeSecondaryTokens(tokensConfig: AccessTokens) {
        this.initializeAccessToken(AccessTokenConsumer.OneDrive, 'api/refreshOneDriveToken/v2', tokensConfig && tokensConfig.OneDrive, this.contextService.sharedContext.oneDriveResourceId);
        this.initializeAccessToken(AccessTokenConsumer.SharePoint, 'api/refreshSharePointToken/v2', tokensConfig && tokensConfig.SharePoint, this.contextService.sharedContext.sharePointResourceId);
        this.initializeAccessToken(AccessTokenConsumer.Exchange, 'api/refreshExchangeToken/v2', tokensConfig && tokensConfig.Exchange);
    }

    // This is function that will take care of creating token record
    private initializeAccessToken(id: AccessTokenConsumer, refreshUrl: string, token: AccessToken, resourceUrl = '') {
        token = token || { TokenValue: '' };
        if (token.TokenValue && !token.ExpiryMs) {
            token.ExpiryMs = this.getDefaultExpiry();
        }

        const tokenInfo: AccessTokenInfo = {
            refreshEndpoint: refreshUrl,
            token,
            resourceUrl
        };

        this.accessTokens.set(this.getTokenKey(id, resourceUrl), tokenInfo);

        if (refreshUrl) {
            this.refreshApiUrls.set(id, refreshUrl);
        }
    }

    private get messageResponses(): ScopableObservable<CAPLoginMessage> {
        return this._messageResponses.asScopableObservable();
    }

    private createResourceWithScopes(resourceId: string, scopes: Array<string>): string {
        if (!scopes || scopes.length === 0) {
            return resourceId;
        }

        return scopes.map(s => `${resourceId}/${s}`).join(' ');
    }
}
